import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCartPricing } from '../../../../contexts/CartPricingContext';
import { centsToDollarsNumber } from '../../../../lib/util';
import * as types from '../../../../types';
import styles from './Confirm.module.scss';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import { useLocale } from '../../../../contexts/LocaleContext';
import { TFunction } from 'i18next';
import PricesInCurrencyMessage from '../../../../components/PricesInCurrencyMessage';
import { isEnrolledInPositionDiscount } from '../../../../lib/positionDiscount';
import useCartHasOnlyFreeTrialBundles from '../../../../hooks/useCartHasOnlyFreeTrialBundles';
import useFreeTrialShippingPriceInCents from '../../../../hooks/useFreeTrialShippingPriceInCents';

interface PricingSummaryProps {
  selectedShippingOption?: types.IShippingOption;
}

function displayShippingPrice(
  shippingPriceInCents: number | undefined,
  currencyFormatter: Intl.NumberFormat,
  t: TFunction,
) {
  if (!shippingPriceInCents) {
    return t('free');
  }
  return currencyFormatter.format(centsToDollarsNumber(shippingPriceInCents));
}

function getDiscountLabel(cartPricing: types.CartPricing, showCartTest: boolean, t: TFunction) {
  // Position discounts are recurring, not one-time
  if (isEnrolledInPositionDiscount(cartPricing)) {
    return t('multi_pet_discount');
  }
  return showCartTest ? t('one_time_promo') : t('discount');
}

export default function PricingSummary({ selectedShippingOption }: PricingSummaryProps) {
  const { t } = useTranslation();
  const cartPricing = useCartPricing();
  const showCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const { currencyFormatter } = useLocale();
  const onlyFreeTrials = useCartHasOnlyFreeTrialBundles();
  const freeTrialShippingInCents = useFreeTrialShippingPriceInCents();
  const includeFreeTrialShipping = onlyFreeTrials;

  return (
    <div className={styles.cartSummary}>
      <PricesInCurrencyMessage />
      <div className={styles.row}>
        <div>{t('subtotal')}</div>
        <div>{currencyFormatter.format(centsToDollarsNumber(cartPricing.subtotalInCents))}</div>
      </div>
      {!showCartTest && selectedShippingOption && (
        <div className={styles.row}>
          <div>{t('shipping')}</div>
          <div>
            {displayShippingPrice(
              includeFreeTrialShipping && !cartPricing.shippingInCents
                ? freeTrialShippingInCents
                : cartPricing.shippingInCents,
              currencyFormatter,
              t,
            )}
          </div>
        </div>
      )}
      {!!cartPricing.appliedDiscountInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{getDiscountLabel(cartPricing, showCartTest, t)}</div>
          <div>
            {'-'}
            {currencyFormatter.format(centsToDollarsNumber(cartPricing.appliedDiscountInCents))}
          </div>
        </div>
      )}
      <div className={styles.row}>
        <div>{showCartTest ? t('tax') : t('taxes')}</div>
        <div>{currencyFormatter.format(centsToDollarsNumber(cartPricing.taxInCents ?? 0))}</div>
      </div>
      {showCartTest && selectedShippingOption && (
        <div className={styles.row}>
          <div>{t('shipping')}</div>
          <div>
            {displayShippingPrice(
              includeFreeTrialShipping && !cartPricing.shippingInCents
                ? freeTrialShippingInCents
                : cartPricing.shippingInCents,
              currencyFormatter,
              t,
            )}
          </div>
        </div>
      )}
      {!!cartPricing.appliedGiftCardAmountInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>
            {t('gift_card')}
            {cartPricing.totalValueOfRedeemedGiftCardInCents && (
              <span>
                {t('amount_value', {
                  amount: currencyFormatter.format(
                    centsToDollarsNumber(cartPricing.totalValueOfRedeemedGiftCardInCents),
                  ),
                })}
              </span>
            )}
          </div>
          <div>
            {'-'}
            {currencyFormatter.format(centsToDollarsNumber(cartPricing.appliedGiftCardAmountInCents))}
          </div>
        </div>
      )}
      {!!cartPricing.appliedAccountBalanceInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{t('account_balance')}</div>
          <div>
            {'-'}
            {currencyFormatter.format(centsToDollarsNumber(cartPricing.appliedAccountBalanceInCents))}
          </div>
        </div>
      )}
      {!!cartPricing.upgradeCreditAmountInCents && (
        <div className={classNames(styles.row, styles.cartSavings)}>
          <div>{t('credits')}</div>
          <div>
            {'-'}
            {currencyFormatter.format(centsToDollarsNumber(cartPricing.upgradeCreditAmountInCents))}
          </div>
        </div>
      )}
      <div className={classNames(styles.row, styles.cartTotal)}>
        <div>{includeFreeTrialShipping ? t('due_today') : t('total')}</div>
        <div>
          {(() => {
            const shippingAddon =
              includeFreeTrialShipping && !cartPricing.shippingInCents ? freeTrialShippingInCents : 0;
            const total = cartPricing.totalInCents + shippingAddon;
            return total === 0 ? t('free') : currencyFormatter.format(centsToDollarsNumber(total));
          })()}
        </div>
      </div>
    </div>
  );
}
